import * as React from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { fetchDailyPulseByGender } from '@src/api';
import { DateRangePicker, DollarFormatter, Page, PercentageFormatter } from '@src/components';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import { formatDateToYMD } from '@src/utils/dateUtils';

const GENDER_LABELS = { male: 'Male', female: 'Female', unknown: 'Unknown' };
const GENDER_ORDER = ['female', 'male', 'unknown'];

const safeNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const safeDiv = (numerator, denominator) => {
  const num = safeNumber(numerator);
  const denom = safeNumber(denominator);
  return denom ? num / denom : 0;
};

const getCurrentMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

function aggregateGenderRows(summaryRows = [], selectedClientId = '', selectedVertical = '') {
  const buckets = new Map();

  (summaryRows || []).forEach((row = {}) => {
    const clientId = row.client_id == null ? '' : String(row.client_id);
    if (selectedClientId && clientId !== String(selectedClientId)) {
      return;
    }
    const vertical = (row.vertical || row.vertical_label || '').trim();
    if (selectedVertical && vertical !== selectedVertical) {
      return;
    }
    const gender = String(row.gender || 'unknown').toLowerCase();

    if (!buckets.has(gender)) {
      buckets.set(gender, {
        gender,
        label: GENDER_LABELS[gender] || gender,
        total_spend_sum: 0,
        total_leads_sum: 0,
        booked_sum: 0,
        scheduled_sum: 0,
        issues_sum: 0,
        demos_sum: 0,
        gross_sales_sum: 0,
        net_sales_sum: 0,
        net_amount_sum: 0,
      });
    }

    const bucket = buckets.get(gender);
    bucket.total_spend_sum += safeNumber(row.total_spend_sum);
    bucket.total_leads_sum += safeNumber(row.total_leads_sum);
    bucket.booked_sum += safeNumber(row.booked_sum);
    bucket.scheduled_sum += safeNumber(row.scheduled_sum);
    bucket.issues_sum += safeNumber(row.issues_sum);
    bucket.demos_sum += safeNumber(row.demos_sum);
    bucket.gross_sales_sum += safeNumber(row.gross_sales_sum || row.gross_sold_sum);
    bucket.net_sales_sum += safeNumber(row.net_sales_sum || row.net_sold_sum);
    bucket.net_amount_sum += safeNumber(row.net_amount_sum);
  });

  return Array.from(buckets.values()).sort(
    (a, b) => GENDER_ORDER.indexOf(a.gender) - GENDER_ORDER.indexOf(b.gender)
  );
}

function buildTotals(rows = []) {
  return rows.reduce(
    (acc, row = {}) => {
      acc.spend += safeNumber(row.total_spend_sum);
      acc.leads += safeNumber(row.total_leads_sum);
      acc.booked += safeNumber(row.booked_sum);
      acc.scheduled += safeNumber(row.scheduled_sum);
      acc.issues += safeNumber(row.issues_sum);
      acc.demos += safeNumber(row.demos_sum);
      acc.grossSold += safeNumber(row.gross_sales_sum);
      acc.netSold += safeNumber(row.net_sales_sum);
      acc.netAmount += safeNumber(row.net_amount_sum);
      return acc;
    },
    {
      spend: 0,
      leads: 0,
      booked: 0,
      scheduled: 0,
      issues: 0,
      demos: 0,
      grossSold: 0,
      netSold: 0,
      netAmount: 0,
    }
  );
}

const compactTableSx = {
  '& .MuiTableCell-root': { py: 0.75, px: 1.25, whiteSpace: 'nowrap' },
  '& .MuiTableHead-root .MuiTableCell-root': {
    fontWeight: 700,
    bgcolor: 'background.neutral',
  },
};

function MetricsRow({ label, row, totals }) {
  const spend = safeNumber(row.total_spend_sum);
  const leads = safeNumber(row.total_leads_sum);
  const booked = safeNumber(row.booked_sum);
  const issues = safeNumber(row.issues_sum);
  const demos = safeNumber(row.demos_sum);
  const netSold = safeNumber(row.net_sales_sum);
  const netAmount = safeNumber(row.net_amount_sum);
  const totalLeads = safeNumber(totals?.leads);

  return (
    <TableRow>
      <TableCell sx={{ fontWeight: 600 }}>{label}</TableCell>
      <TableCell><PercentageFormatter value={safeDiv(leads, totalLeads)} /></TableCell>
      <TableCell><DollarFormatter value={spend} /></TableCell>
      <TableCell>{leads}</TableCell>
      <TableCell><DollarFormatter value={safeDiv(spend, leads)} /></TableCell>
      <TableCell>{booked}</TableCell>
      <TableCell><PercentageFormatter value={safeDiv(booked, leads)} /></TableCell>
      <TableCell>{issues}</TableCell>
      <TableCell>{demos}</TableCell>
      <TableCell>{netSold}</TableCell>
      <TableCell><PercentageFormatter value={safeDiv(netSold, demos)} /></TableCell>
      <TableCell><DollarFormatter value={netAmount} /></TableCell>
      <TableCell><DollarFormatter value={safeDiv(netAmount, netSold)} /></TableCell>
      <TableCell><DollarFormatter value={safeDiv(netAmount, leads)} /></TableCell>
      <TableCell><PercentageFormatter value={safeDiv(spend, netAmount)} /></TableCell>
    </TableRow>
  );
}

export default function GenderPerformancePage() {
  const defaultRange = React.useMemo(() => getCurrentMonthRange(), []);
  const [startDate, setStartDate] = React.useState(defaultRange.start);
  const [endDate, setEndDate] = React.useState(defaultRange.end);
  const [selectedClientId, setSelectedClientId] = React.useState('');
  const [selectedVertical, setSelectedVertical] = React.useState('');
  const [rawRows, setRawRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const {
    options: clientOptions,
    loading: clientsLoading,
    handleInputChange: handleClientInputChange,
    findOptionByValue: findClientOptionByValue,
  } = useClientAutocomplete();

  React.useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = {
          date_after: formatDateToYMD(startDate),
          date_before: formatDateToYMD(endDate),
          ...(selectedClientId ? { client_id: selectedClientId } : {}),
          ...(selectedVertical ? { vertical: selectedVertical } : {}),
        };
        const response = await fetchDailyPulseByGender(payload);
        if (!active) {
          return;
        }
        setRawRows(Array.isArray(response?.data) ? response.data : []);
      } catch (err) {
        console.error('Failed to load gender performance data', err);
        if (active) {
          setError('Failed to load gender performance data.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [startDate, endDate, selectedClientId, selectedVertical]);

  const rows = React.useMemo(
    () => aggregateGenderRows(rawRows, selectedClientId, selectedVertical),
    [rawRows, selectedClientId, selectedVertical]
  );
  const totals = React.useMemo(() => buildTotals(rows), [rows]);
  const verticalOptions = React.useMemo(
    () =>
      Array.from(
        new Set(
          rawRows
            .map((row = {}) => (row?.vertical_label || row?.vertical || '').trim())
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b)),
    [rawRows]
  );

  const handleDateRangeChange = React.useCallback((ranges) => {
    const nextStart = ranges?.selection?.startDate;
    const nextEnd = ranges?.selection?.endDate;
    if (nextStart instanceof Date && nextEnd instanceof Date) {
      setStartDate(nextStart);
      setEndDate(nextEnd);
    }
  }, []);

  return (
    <Page title="Gender Performance" fullWidth isLoading={loading}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5">Gender Performance</Typography>
          <Typography variant="body2" color="text.secondary">
            Conversion and revenue per lead split by inferred gender (from first
            name via SSA names data, 90% confidence threshold). Use these
            deltas to inform Facebook value-rule bids.
          </Typography>
        </Box>

        <Paper sx={{ p: { xs: 1.25, md: 1.5 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <Box sx={{ minWidth: { xs: '100%', md: 320 } }}>
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onChange={handleDateRangeChange}
                style={{ width: '100%' }}
              />
            </Box>
            <Autocomplete
              sx={{ minWidth: 260 }}
              options={clientOptions}
              loading={clientsLoading}
              value={findClientOptionByValue(selectedClientId)}
              onInputChange={handleClientInputChange}
              onChange={(_event, option) => setSelectedClientId(option ? String(option.value) : '')}
              isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
              getOptionLabel={(option) => option?.label || ''}
              renderInput={(params) => (
                <TextField {...params} label="Client" placeholder="All clients" />
              )}
            />
            <Autocomplete
              sx={{ minWidth: 260 }}
              options={verticalOptions}
              value={selectedVertical || null}
              onChange={(_event, option) => setSelectedVertical(option || '')}
              getOptionLabel={(option) => option || ''}
              renderInput={(params) => (
                <TextField {...params} label="Vertical" placeholder="All verticals" />
              )}
            />
          </Stack>
        </Paper>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Card>
          <CardHeader
            title="Performance by Gender"
            subheader="Aggregated across the selected date range, client, and vertical."
          />
          <CardContent>
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={compactTableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell>Gender</TableCell>
                    <TableCell>Lead Mix</TableCell>
                    <TableCell>Spend</TableCell>
                    <TableCell>Leads</TableCell>
                    <TableCell>CPL</TableCell>
                    <TableCell>Booked</TableCell>
                    <TableCell>Set Rate</TableCell>
                    <TableCell>Issues</TableCell>
                    <TableCell>Demos</TableCell>
                    <TableCell>Net Sold</TableCell>
                    <TableCell>Close Rate</TableCell>
                    <TableCell>Net Amount</TableCell>
                    <TableCell>Avg Deal</TableCell>
                    <TableCell>RPL</TableCell>
                    <TableCell>Cost of Marketing</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length ? (
                    <>
                      {rows.map((row) => (
                        <MetricsRow
                          key={row.gender}
                          label={row.label}
                          row={row}
                          totals={totals}
                        />
                      ))}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                        <TableCell />
                        <TableCell sx={{ fontWeight: 700 }}>
                          <DollarFormatter value={totals.spend} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{totals.leads}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <DollarFormatter value={safeDiv(totals.spend, totals.leads)} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{totals.booked}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <PercentageFormatter value={safeDiv(totals.booked, totals.leads)} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{totals.issues}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{totals.demos}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{totals.netSold}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <PercentageFormatter value={safeDiv(totals.netSold, totals.demos)} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <DollarFormatter value={totals.netAmount} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <DollarFormatter value={safeDiv(totals.netAmount, totals.netSold)} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <DollarFormatter value={safeDiv(totals.netAmount, totals.leads)} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <PercentageFormatter value={safeDiv(totals.spend, totals.netAmount)} />
                        </TableCell>
                      </TableRow>
                    </>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={15} align="center">
                        <Typography variant="body2" color="text.secondary">
                          {loading ? 'Loading…' : 'No data for the selected range.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Stack>
    </Page>
  );
}
